import { Middleware } from "redux";
import {
  loadCustomMix,
  loadMixFromInitialTrack,
  loadProjectLaneAction,
  loadLaneAction,
  loadMixFromInitialTrackCache,
  saveInitialTrackCache,
  loadAllHDAudioFiles
} from "../actions/actionCreators";
import {
  LOAD_CUSTOM_MIX_PROJECT,
  LOAD_MIX,
  LOAD_LANE,
  UPLOAD_LANE,
  LOAD_EXTERNAL_LANE,
  SAVE_INITIAL_TRACK_CACHE,
  LOAD_HD_STEMS
} from "../actions/actionTypes";
import { AudioMaster } from "../audio/AudioMaster";
import { Instrument, InstrumentCategories, MainStore } from "../store";
import { uploadInstrument } from "../store/test_initial_store";

/**
 * Provides access to global variables like audioMaster
 * @param audioMaster
 * @constructor
 */
export function ResolveGlobalsMiddleware(audioMaster: AudioMaster): Middleware {
  return store => next => action => {
    switch (action.type) {
      case LOAD_MIX:
        if (action.fromCache) {
          const loadAction = loadMixFromInitialTrackCache(
            action.projectId,
            action.paidAccess,
            audioMaster,
            action.onRehydrated,
            action.onLoaded
          );
          // @ts-ignore
          store.dispatch(loadAction);
        } else {
          const loadAction = loadMixFromInitialTrack(
            action.projectId,
            audioMaster,
            action.onRehydrated,
            action.onLoaded
          );
          // @ts-ignore
          store.dispatch(loadAction);
        }
        break;
      case LOAD_HD_STEMS:
        loadAllHDAudioFiles(
          audioMaster,
          action.stemFidelity,
          store.dispatch,
          store.getState,
          action.onLoaded
        );
        break;
      case LOAD_CUSTOM_MIX_PROJECT:
        const loadCustomMixAction = loadCustomMix(
          action.customMixId,
          action.versionNumber,
          action.paidAccess,
          audioMaster,
          action.onRehydrated,
          action.onLoaded
        );
        // @ts-ignore
        store.dispatch(loadCustomMixAction);
        break;
      case SAVE_INITIAL_TRACK_CACHE:
        const saveInitialTrackCacheAction = saveInitialTrackCache(
          action.trackId,
          action.onSuccess,
          action.onFail
        );
        //@ts-ignore
        store.dispatch(saveInitialTrackCacheAction);
        break;
      case LOAD_LANE: {
        const state: MainStore = store.getState();
        const projectSpec = state.project.initialTrackData;

        const blockData = state.project.initialTrackData.blockFiles.find(
          x => x.id === action.id
        );
        const laneData = state.project.initialTrackData.lanes.find(
          x => x.id === action.id
        );

        const laneInfo = action.isBlock ? blockData : laneData;

        if (laneInfo) {
          const loadLane = loadProjectLaneAction(
            projectSpec,
            laneInfo,
            audioMaster,
            state.editorState.nthProjectLoaded
          );
          // @ts-ignore
          store.dispatch(loadLane);
        } else {
          console.warn("COULD'T FIND LANE WITH ID: ", action.id);
        }
        break;
      }
      case UPLOAD_LANE: {
        const state: MainStore = store.getState();
        const projectSpec = state.project.initialTrackData;
        const defaultUploadInstrument = {
          ...uploadInstrument,
          name: action.name
        };

        // the regex matches the text between the last underscore and the end of the string
        const laneInstrument = /_\s*([^_]+)$/.exec(action.name)?.[1];

        const instrumentLibrary = action.instruments ?? projectSpec.instruments;
        const betterInstrumentMatch: Instrument = instrumentLibrary.find(
          instrument => instrument.name === laneInstrument
        );
        const loadLane = loadLaneAction(
          action.name,
          projectSpec.bpm,
          projectSpec.beatsPerMeasure,
          0,
          action.url,
          action.url,
          action.url,
          null,
          betterInstrumentMatch ?? defaultUploadInstrument,
          audioMaster,
          state.editorState.nthProjectLoaded
        );
        // @ts-ignore
        store.dispatch(loadLane);
        break;
      }
      case LOAD_EXTERNAL_LANE: {
        const state: MainStore = store.getState();
        const projectSpec = state.project.initialTrackData;

        // Create instrument object from the external stem data
        const externalInstrument: Instrument = {
          id: action.stemName,
          name: action.instrumentName,
          category: action.instrumentCategory as InstrumentCategories
        };

        // Display name includes the source track title for clarity
        const displayName = `${action.instrumentName} (from ${action.trackTitle})`;

        const loadLane = loadLaneAction(
          displayName,
          projectSpec.bpm,
          projectSpec.beatsPerMeasure,
          0,
          action.stemMp3File,
          action.stemMp3File, // use mp3 for HQ as well
          action.stemWavFile || action.stemMp3File,
          null,
          externalInstrument,
          audioMaster,
          state.editorState.nthProjectLoaded,
          action.initialBeat // use the external stem's initialBeat for proper alignment
        );
        // @ts-ignore
        store.dispatch(loadLane);
        break;
      }
    }

    const result = next(action);
    return result;
  };
}
